/**
 * AssetPickerGenerateTab - Inline AI image generation tab.
 *
 * Users select a source image, enter a prompt, pick a ratio,
 * and generate via the V2 Gemini API. After generation, they
 * can confirm to use the result as an AI reference.
 */

import { useState, useCallback } from 'react';
import { Sparkles, AlertTriangle } from 'lucide-react';
import {
  Button, Textarea, Alert, AlertDescription, Spinner,
} from '@/components/v2/ui';
import type { AssetPoolItem } from '@/lib/api/products';
import { generateImageV2 } from '@/lib/api/generation';
import { GenerateSourceRow } from './GenerateSourceRow';
import { GenerateResultView } from './GenerateResultView';

const RATIOS = ['1:1', '4:3', '3:4', '16:9', '9:16'] as const;

interface AssetPickerGenerateTabProps {
  assets: AssetPoolItem[];
  productId: string;
  slotNumber: number;
  onSelect: (generatedImageId: string) => void;
}

export function AssetPickerGenerateTab({
  assets, productId, slotNumber: _slotNumber, onSelect,
}: AssetPickerGenerateTabProps) {
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [ratio, setRatio] = useState('1:1');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultId, setResultId] = useState<string | null>(null);

  const selectedAsset = assets.find((a) => a.id === sourceId);
  const canGenerate = !!selectedAsset?.thumbnail_url && prompt.trim().length > 0;

  const handleGenerate = useCallback(async () => {
    if (!selectedAsset?.thumbnail_url || !prompt.trim()) return;
    setIsGenerating(true);
    setError(null);
    try {
      const file = await fetchAsFile(selectedAsset.thumbnail_url);
      const res = await generateImageV2({
        image: file,
        prompt_input: prompt.trim(),
        ratio,
        resolution: '1k',
        delivery_method: 'wait',
      });
      if (res.success && res.image_id) {
        setResultId(res.image_id);
      } else {
        setError(res.error || res.message || 'Generierung fehlgeschlagen.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler.');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedAsset, prompt, ratio]);

  const handleReset = () => { setResultId(null); setError(null); };

  if (resultId) {
    return (
      <GenerateResultView
        imageId={resultId}
        productId={productId}
        onConfirm={() => onSelect(resultId)}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <Alert variant="warning" icon={<AlertTriangle className="h-4 w-4" />}>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <GenerateSourceRow assets={assets} selectedId={sourceId} onSelect={setSourceId} />
      <PromptField value={prompt} onChange={setPrompt} />
      <RatioSelector value={ratio} onChange={setRatio} />

      <Button
        variant="primary"
        disabled={!canGenerate || isGenerating}
        onClick={handleGenerate}
        className="w-full"
      >
        {isGenerating ? <Spinner className="h-4 w-4 mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
        {isGenerating ? 'Generiert...' : 'Bild generieren'}
      </Button>
    </div>
  );
}

// ============================================================================
// Helpers (inline, tiny)
// ============================================================================

async function fetchAsFile(url: string): Promise<File> {
  const resp = await fetch(url);
  const blob = await resp.blob();
  return new File([blob], 'source.png', { type: blob.type });
}

function PromptField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
        Prompt
      </label>
      <Textarea
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="z.B. Weisser Hintergrund, professionelle Produktfotografie"
      />
    </div>
  );
}

function RatioSelector({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
        Seitenverhaeltnis
      </label>
      <div className="flex gap-1.5">
        {RATIOS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onChange(r)}
            className={`flex-1 rounded-md py-1.5 text-xs font-medium transition-colors ${
              r === value
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
            }`}
          >
            {r}
          </button>
        ))}
      </div>
    </div>
  );
}
